import {
  DataGrid,
  FilterToolbar,
  formatCurrency,
  formatDate,
  StatusBadge,
  type FilterDef,
  type GridQuery
} from '@ijoin/components';
import {
  GetAnnuityCarrierExportsParams,
  useGetAnnuityCarrierExports
} from '@ijoin/hooks';
import { GridColDef } from '@mui/x-data-grid';
import { keepPreviousData } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';

import { FC, useState } from 'react';

import { CarrierExportActionsCell } from './CarrierExportActionsCell';

const NULLABLE_OPTIONS = [
  { label: 'Missing', value: 'missing' },
  { label: 'Present', value: 'present' }
];

const FILTER_DEFS: FilterDef[] = [
  { label: 'Participant', name: 'participant', type: 'text' },
  { label: 'ID', name: 'id', type: 'text' },
  { label: 'Plan', name: 'planDescription', type: 'text' },
  { label: 'Account Number', name: 'accountNumber', type: 'text' },
  { label: 'RK Plan ID', name: 'externalPlanId', type: 'text' },
  { label: 'File Identifier', name: 'fileIdentifier', type: 'text' },
  {
    label: 'File Identifier Status',
    name: 'fileIdentifierStatus',
    options: NULLABLE_OPTIONS,
    type: 'select'
  },
  {
    label: 'Sent To Carrier Status',
    name: 'sentToCarrierStatus',
    options: NULLABLE_OPTIONS,
    type: 'select'
  },
  { label: 'Trade Identifier', name: 'tradeIdentifier', type: 'text' },
  {
    label: 'Buy/Sell',
    name: 'buySellCode',
    options: [
      { label: 'Buy', value: 'B' },
      { label: 'Sell', value: 'S' }
    ],
    type: 'select'
  },
  {
    label: 'Roth',
    name: 'isRoth',
    options: [
      { label: 'Yes', value: 'true' },
      { label: 'No', value: 'false' }
    ],
    type: 'select'
  },
  {
    label: 'Processed',
    name: 'isProcessed',
    options: [
      { label: 'Processed', value: 'true' },
      { label: 'Pending', value: 'false' }
    ],
    type: 'select'
  },
  {
    format: 'currency',
    label: 'Amount',
    min: -Number.MAX_SAFE_INTEGER,
    name: 'totalTransAmt',
    type: 'number'
  },
  {
    endName: 'endPostDate',
    label: 'Post Date',
    startName: 'startPostDate',
    type: 'dateRange'
  },
  {
    endName: 'endCreatedAt',
    label: 'Created',
    startName: 'startCreatedAt',
    type: 'dateRange'
  }
];

export const CarrierExportsTab: FC<{
  annuityIntegrationId: string;
}> = props => {
  const [query, setQuery] = useState<GridQuery>();

  const exports = useGetAnnuityCarrierExports(
    props.annuityIntegrationId,
    query as GetAnnuityCarrierExportsParams,
    { query: { enabled: !!query, placeholderData: keepPreviousData } }
  );

  const columns: GridColDef[] = [
    {
      field: 'createdAt',
      flex: 1,
      headerName: 'Date',
      valueFormatter: value =>
        value ? formatDate(value, { format: 'MM/DD/YYYY h:mm:ss A' }) : ''
    },
    { field: 'id', flex: 0.8, headerName: 'ID', type: 'number' },
    {
      field: 'participantName',
      flex: 1,
      headerName: 'Participant',
      sortable: false
    },
    {
      field: 'planDescription',
      flex: 1,
      headerName: 'Plan',
      renderCell: params => (
        <Link
          data-testid={`planDescription-${params.row.id}`}
          params={{
            advisoryOrganizationId: params.row.advisoryOrganizationId,
            planId: params.row.planId
          }}
          to='/organizations/$advisoryOrganizationId/plans/$planId'>
          {params.value}
        </Link>
      ),
      sortable: false
    },
    {
      field: 'accountNumber',
      flex: 1,
      headerName: 'Account Number',
      sortable: false
    },
    {
      field: 'externalPlanId',
      flex: 1,
      headerName: 'RK Plan ID',
      sortable: false
    },
    {
      field: 'fileIdentifier',
      flex: 1,
      headerName: 'File Identifier',
      sortable: false
    },
    {
      field: 'sentToCarrierDate',
      flex: 1,
      headerName: 'Sent To Carrier',
      valueFormatter: value =>
        value ? formatDate(value, { format: 'MM/DD/YYYY h:mm:ss A' }) : ''
    },
    {
      field: 'tradeIdentifier',
      flex: 1,
      headerName: 'Trade Identifier',
      sortable: false
    },
    {
      field: 'buySellCode',
      flex: 0.7,
      headerName: 'Buy/Sell',
      sortable: false,
      valueFormatter: value =>
        value === 'B' ? 'Buy' : value === 'S' ? 'Sell' : ''
    },
    {
      align: 'center',
      field: 'isRoth',
      flex: 0.6,
      headerAlign: 'center',
      headerName: 'Roth',
      renderCell: params =>
        params.value ? <StatusBadge severity='info'>Roth</StatusBadge> : null,
      sortable: false
    },
    {
      align: 'center',
      field: 'isProcessed',
      flex: 0.8,
      headerAlign: 'center',
      headerName: 'Processed',
      renderCell: params => (
        <StatusBadge severity={params.value ? 'success' : 'info'}>
          {params.value ? 'Processed' : 'Pending'}
        </StatusBadge>
      ),
      sortable: false
    },
    {
      field: 'totalTransAmt',
      flex: 1,
      headerName: 'Amount',
      type: 'number',
      valueFormatter: value => (value == null ? '' : formatCurrency(value))
    },
    {
      field: 'postDate',
      flex: 1,
      headerName: 'Post Date',
      valueFormatter: value =>
        value ? formatDate(value, { format: 'MM/DD/YYYY', useUtc: true }) : ''
    },
    {
      align: 'center',
      field: 'actions',
      headerName: '',
      renderCell: params => (
        <CarrierExportActionsCell
          annuityIntegrationId={props.annuityIntegrationId}
          exportRow={params.row}
        />
      ),
      sortable: false,
      width: 65
    }
  ];

  return (
    <DataGrid
      autoHeight={false}
      columns={columns}
      disableColumnMenu
      disableRowSelectionOnClick
      filterDefs={FILTER_DEFS}
      getRowId={row => row.id}
      initialSort={[{ field: 'createdAt', sort: 'desc' }]}
      loading={!query || exports.isFetching}
      onClear={setQuery}
      onSearch={setQuery}
      pageSize={25}
      pageSizeOptions={[10, 25, 50, 100]}
      paginationMode='server'
      rowCount={exports.data?.pagination?.total ?? 0}
      rows={exports.data?.data ?? []}
      showColumnMenu
      showToolbar
      slotProps={{
        root: {
          'data-component': 'dataGrid',
          'data-testid': 'carrierExports'
        },
        toolbar: { onRefresh: exports.refetch }
      }}
      slots={{ toolbar: FilterToolbar }}
      sortingMode='server'
      sx={{ flex: 1, minHeight: 0 }}
    />
  );
};

CarrierExportsTab.displayName = 'CarrierExportsTab';
